import { Injectable } from '@angular/core';
import { WorkflowRestService } from '@app/services/rest/workflow-rest.service';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import { ContentBlockReportItem, ListReportItem, ReportConfig } from '@interfaces/reports';

export type ResolvedKey = {
	itemId: string;
	type: 'completed' | 'aggregate';
	prefix: string;
};

/**
 * Interface zum Abrufen und aggregieren der WFS Daten zur Anzeige der Reports
 */
export interface ReportDataResolver {
	/**
	 * Datenobjekt sammelt daten der WFS als
	 *
	 * * `wfs.completed.*`
	 * * `wfs.aggregate.*`
	 *
	 */
	readonly data: SessionItemData;

	/**
	 * Löst Property-Keys auf und gibt den Wert der Property zurück.
	 *
	 * Keys sind vollständig mit `wfs.aggregate.*` bzs. `wfs.completed.*` anzugeben
	 *
	 * @param key Key zur Property
	 */
	resolveProperty(key: string): Promise<unknown>;

	/**
	 * Löst WFS Daten anhand von Property-Keys auf und gibt das WFS Datenobjekt zurück.
	 *
	 * Keys sind vollständig mit `wfs.aggregate.*` bzs. `wfs.completed.*` anzugeben
	 *
	 * @param key Key zur Property
	 */
	resolveData(key: string): Promise<unknown>;

	/**
	 * Löst WFS Daten anhand von Property-Keys auf und gibt das WFS Datenobjekt zurück.
	 *
	 * @param key ResolvedKey mit WFS-Id, Typ und Prefix
	 */
	resolve(key: ResolvedKey): Promise<unknown>;
}

@Injectable({
	providedIn: 'root'
})
export class ResolveReportDataService {

	constructor(private workflowRestService: WorkflowRestService) { }

	/**
	 * WorkflowItemId und Data-Type aus dem Key extrahieren
	 *
	 * @param key Report-Key
	 * @returns ResolvedKey oder undefined
	 */
	private resolveKey(key: string): ResolvedKey | undefined {
		const parts = key.split('.', 3);
		if (parts.length >= 2) {
			switch (parts[1]) {
				case 'completed':
					return { itemId: parts[0], type: 'completed', prefix: `${parts[0]}.completed` };
				case 'aggregate':
					return { itemId: parts[0], type: 'aggregate', prefix: `${parts[0]}.aggregate` };
				default:
					return undefined;
			}
		}
		return undefined;
	}

	/**
	 * ResolvedKeys aus dem Report extrahieren
	 *
	 * @param report ListReportItem
	 * @returns ResolveKeys
	 */
	private resolveListReportKeys(report: ListReportItem): ResolvedKey[] {
		let prefix = '';
		if (report.keyPrefix) {
			// Shortcut: Prefix ist schon vollständig
			const resolved = this.resolveKey(report.keyPrefix);
			if (resolved) {
				return [resolved];
			}
			prefix = report.keyPrefix + '.';
		}

		const prefixes = new Map<string, ResolvedKey>();
		for (const entry of report.entries) {
			const resolved = this.resolveKey(prefix + entry.key);
			if (!resolved) {
				continue;
			}
			prefixes.set(resolved.prefix, resolved);
		}
		return Array.from(prefixes.values());
	}


	private resolveContentBlockReportKeys(report: ContentBlockReportItem): ResolvedKey[] {
		return report.requiredKeys.map(this.resolveKey);
	}
	/**
	 * ResolvedKeys aus dem Report extrahieren
	 *
	 * @param reportConfigs ReportConfigs
	 * @returns ResolveKeys
	 */
	resolveRequiredKeys(reportConfigs: ReportConfig[]): ResolvedKey[] {
		const prefixes = new Map<string, ResolvedKey>();
		for (const reportConfig of reportConfigs) {
			for (const report of reportConfig.reports) {
				let resolvedKeys: ResolvedKey[] = [];
				if (report.type === 'ListReport') {
					resolvedKeys = this.resolveListReportKeys(report as ListReportItem);
				} else if (report.type === 'ContentBlockReport') {
					resolvedKeys = this.resolveContentBlockReportKeys(report as ContentBlockReportItem);
				}
				for (const resolved of resolvedKeys) {
					prefixes.set(resolved.prefix, resolved);
				}
			}
		}
		return Array.from(prefixes.values());
	}

	/**
	 * WFS Daten auflösen
	 *
	 * Gibt es zu einem WFS keine Daten oder es wird ein ungültiger Key angegeben, dann wird ein leeres Objekt zurückgegeben.
	 *
	 * @param projectId Projekt-Id
	 * @param participantId Teilnehmer-Id
	 * @param resolvedKey Key zum WFS
	 */
	private async resolveData(projectId: string, participantId: string, resolvedKey: ResolvedKey): Promise<unknown> {
		switch (resolvedKey.type) {
			case 'aggregate':
			{
				try {
					const aggregate = await this.workflowRestService.getWorkflowItemAggregate$(projectId, participantId, resolvedKey.itemId).toPromise();
					return aggregate?.data;
				} catch (error) {
					console.error(error);
				}
				break;
			}
			case 'completed':
			{
				try {
					const all_completed = await this.workflowRestService.getWorkflowItemData$(projectId, participantId, resolvedKey.itemId).toPromise();
					const last_completed = all_completed.pop();
					return last_completed?.data;
				} catch (error) {
					console.error(error);
				}
				break;
			}
		}
		return {};
	}

	/**
	 * Notwenige Workflow Items für die Reports abrufen
	 *
	 * @param reportConfigs Reports
	 * @param resolver DataResolver
	 * @returns SessionItemData mit allen benötigten Workflow Items an den entsprechenden Prefixes
	 */
	async resolveRequiredData(reportConfigs: ReportConfig[], resolver: ReportDataResolver): Promise<SessionItemData> {
		const requiredKeys = this.resolveRequiredKeys(reportConfigs);

		for (const requiredKey of requiredKeys) {
			await resolver.resolve(requiredKey);
		}
		return resolver.data;
	}

	createDataResolver(projectId: string, participantId: string): ReportDataResolver {
		const data = new SessionItemData();
		const resolver: ReportDataResolver = {
			get data() {
				return data;
			},

			resolveProperty: async (key: string) => {
				await resolver.resolveData(key);
				return data.getProperty(key);
			},

			resolveData: async (key: string) => {
				const resolvedKey = this.resolveKey(key);
				if (!resolvedKey) {
					return {};
				}
				return await resolver.resolve(resolvedKey);
			},

			resolve: async (resolvedKey: ResolvedKey) => {
				let resolved = data.getProperty(resolvedKey.prefix);
				if (!resolved) {
					resolved = await this.resolveData(projectId, participantId, resolvedKey);
					data.setProperty(resolvedKey.prefix, resolved);
				}
				return resolved;
			}
		};

		return resolver;
	}


}
